<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useChallengeConfigurations } from "@/composables/tasks/challenge/useChallengeConfigurations";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";
import { useNotifications } from "@/composables/useNotifications";
import { availableTags as availableTagsStore } from "@/stores/v2/tasks/tags";
import { getChallengeMaterialDisplayItems } from "@/utils/v2/tasks/display";

import { BathChallengeConfiguration, isSimplifiedBath, isStandardBath } from "@/types/v2/task/challenge/configuration.types";
import { GroupTag, TankGroup, VaccineMethod, Tank } from "@/types/v2";

import JustificationModal from "@/components/v2/Modal/JustificationModal.vue";
import WeightIcon from "@/components/v2/UI/Icons/WeightIcon.vue";

interface Props {
    configurations: BathChallengeConfiguration[],
    vaccineMethods?: VaccineMethod[],
    tanks: Tank[]
}

// eslint-disable-next-line no-undef
const props = defineProps<Props>();
// eslint-disable-next-line no-undef
const emit = defineEmits<{
    openWeightModal: [configuration: BathChallengeConfiguration]
}>();

const {
    configurations: allConfigurations,
    startBathProcess,
    finishBathProcess,
    getBathProcessStatus,
    getConfigurationTitle,
    getConfigurationTags,
    updateConfiguration
} = useChallengeConfigurations();

const { recordChange } = useTaskHistory();
const { showSuccess, showError } = useNotifications();

const hasAmountColumn = computed(() => props.configurations.some(c => !isSimplifiedBath(c)));
const tableColspan = computed(() => (hasAmountColumn.value ? 8 : 7));

// Local state for tag selection (standard bath only)
const selectedTags = ref<Record<number, GroupTag | null>>({});
const isUpdating = ref<Record<number, boolean>>({});
const dropdownOpen = ref<Record<number, boolean>>({});

const findNewTag = (config: BathChallengeConfiguration): GroupTag | null => {
    if (!isStandardBath(config)) return null;

    const sourceTags = config.sourceGroup?.tags || [];
    const destinationTags = config.destinationGroup?.tags || [];

    const sourceTagTitles = new Set(
        sourceTags
            .filter(tag => tag.title !== 'Start Tag')
            .map(tag => tag.title)
    );

    const newTag = destinationTags.find(tag =>
        tag.title !== 'Start Tag' && !sourceTagTitles.has(tag.title)
    );

    return newTag || null;
};

const getAllConfigIndex = (configKey: string): number => {
    return allConfigurations.value.findIndex(c => c.key === configKey);
};

// Get bath type label
const getBathTypeLabel = (config: BathChallengeConfiguration): string => {
    if (isSimplifiedBath(config)) return 'Simplified Bath';
    if (isStandardBath(config)) return 'Standard Bath';
    return 'Bath';
};

// Get materials display
const getMaterialsDisplay = (config: BathChallengeConfiguration) => {
    return getChallengeMaterialDisplayItems(config, 'Bath');
};

// Handle starting bath process
const handleStartProcess = async (configKey: string) => {
    const allConfigIndex = getAllConfigIndex(configKey);
    if (allConfigIndex === -1) return;
    const config = allConfigurations.value[allConfigIndex] as BathChallengeConfiguration | undefined;
    if (!config) return;

    await startBathProcess(allConfigIndex);
};

const handleFinishProcess = async (configKey: string) => {
    const allConfigIndex = getAllConfigIndex(configKey);
    if (allConfigIndex === -1) return;
    const config = allConfigurations.value[allConfigIndex] as BathChallengeConfiguration | undefined;
    if (!config) return;

    await finishBathProcess(allConfigIndex);
};

// Calculate elapsed time for running processes (reactive)
const currentTime = ref(Date.now());
const getElapsedTime = (startedAt: string): string => {
    const startTime = new Date(startedAt).getTime();
    const elapsedMs = currentTime.value - startTime;
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
};

// Calculate actual duration between start and completion
const getActualDuration = (startedAt: string, completedAt: string): string => {
    const startTime = new Date(startedAt).getTime();
    const endTime = new Date(completedAt).getTime();
    const durationMs = endTime - startTime;
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
};

// Update current time every second for elapsed time display
let timeInterval: number;
onMounted(() => {
    timeInterval = setInterval(() => {
        currentTime.value = Date.now();
    }, 1000); // Update every second
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    if (timeInterval) {
        clearInterval(timeInterval);
    }
    document.removeEventListener('click', handleClickOutside);
});

// Format duration for display
const formatDuration = (minutes: number): string => {
    return `${minutes} min`;
};

// Get process status for display
const getProcessStatus = (config: BathChallengeConfiguration) => {
    const status = getBathProcessStatus(config.key);
    if (!status) {
        // If there's no status, treat it as not started
        return { status: 'not_started', label: 'Ready to Start', color: 'gray' };
    }

    // Dynamically handle the statuses
    switch (status.status) {
        case 'not_started':
            return { status: 'not_started', label: 'Ready to Start', color: 'gray' };
        case 'in_progress':
            return { status: 'in_progress', label: 'In Progress', color: 'blue' };
        case 'completed':
            return { status: 'completed', label: 'Completed', color: 'green' };
        default:
            return { status: 'unknown', label: 'Unknown', color: 'gray' };
    }
};

// Handle weight recording
const handleRecordWeight = (config: BathChallengeConfiguration) => {
    emit('openWeightModal', config);
};

// Check if weight recording should be shown for this configuration
const shouldShowWeightButton = (config: BathChallengeConfiguration): boolean => {
    // Show for both simplified and standard baths if they have targetWeightAmount
    return !!(config.targetWeightAmount);
};

// Tag dropdown handlers
const toggleDropdown = (configIndex: number) => {
    dropdownOpen.value[configIndex] = !dropdownOpen.value[configIndex];
};

const closeDropdown = (configIndex: number) => {
    dropdownOpen.value[configIndex] = false;
};

const closeAllDropdowns = () => {
    Object.keys(dropdownOpen.value).forEach(key => {
        dropdownOpen.value[parseInt(key)] = false;
    });
};

// Handle clicks outside dropdown
const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-container')) {
        closeAllDropdowns();
    }
};

// Handle tag selection (standard bath only)
const selectTag = async (configIndex: number, tag: GroupTag) => {
    const config = props.configurations[configIndex];
    if (!config || !isStandardBath(config)) return;

    const allConfigIndex = getAllConfigIndex(config.key);
    if (allConfigIndex === -1) return;

    const oldTag = selectedTags.value[configIndex];
    isUpdating.value[configIndex] = true;

    try {
        const success = await recordChange({
            fieldPath: `configurations[${allConfigIndex}].selectedTag`,
            fieldName: `Tag (${tag.title})`,
          oldValue: {
            config: config.key,
            key: oldTag?.key || 'None',
            value: oldTag?.title || 'None',
          },
          newValue: {
            config: config.key,
            key: tag.key,
            value: tag.title,
          },
          context: 'ChallengeConfiguration',
            contextType: 'edit',
            workflowStage: 'setup',
            requireJustification: true,
            justification: ''
        });

        if (success) {
            selectedTags.value[configIndex] = tag;

            const sourceTags = config.sourceGroup?.tags || [];
            const sourceTagTitles = new Set(
                sourceTags
                    .filter(t => t.title !== 'Start Tag')
                    .map(t => t.title)
            );

            const updatedDestGroup = config.destinationGroup
                ? { ...config.destinationGroup }
                : { key: '', name: '', tags: [] } as TankGroup;

            if (!updatedDestGroup.tags) {
                updatedDestGroup.tags = [];
            } else {
                updatedDestGroup.tags = [...updatedDestGroup.tags];
            }

            updatedDestGroup.tags = updatedDestGroup.tags.filter(t =>
                sourceTagTitles.has(t.title) || t.title === tag.title
            );

            const tagExists = updatedDestGroup.tags.some(t => t.title === tag.title);
            if (!tagExists) {
                updatedDestGroup.tags.push(tag);
            }

            await updateConfiguration(allConfigIndex, -1, 'destinationGroup', updatedDestGroup);

            showSuccess("Tag Updated", `Tag has been changed to "${tag.title}".`);
        }
    } catch (error) {
        console.error('Error updating tag:', error);
        showError("Tag Update Failed", "Failed to update tag. Please try again.");
    } finally {
        isUpdating.value[configIndex] = false;
    }

    closeDropdown(configIndex);
};

// Local state for instructions visibility
const showInstructions = ref<Record<number, boolean>>({});

// Initialize instructions as expanded by default
watch(() => props.configurations, (configs) => {
    if (configs) {
        configs.forEach((config, index) => {
            if (config.instructions && showInstructions.value[index] === undefined) {
                showInstructions.value[index] = true;
            }
        });
    }
}, { immediate: true });

// Toggle instructions visibility
const toggleInstructions = (configIndex: number) => {
    showInstructions.value[configIndex] = !showInstructions.value[configIndex];
};

// Initialize selected tag from destination group (new tag) for standard bath
watch(() => props.configurations, (configs) => {
    if (configs) {
        configs.forEach((config, index) => {
            if (isStandardBath(config) && !selectedTags.value[index]) {
                const newTag = findNewTag(config);
                if (newTag) {
                    selectedTags.value[index] = newTag;
                }
            }
        });
    }
}, { immediate: true });

</script>

<template>
    <div class="w-full">
        <div class="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table class="min-w-full divide-y divide-gray-200">
                <thead>
                <tr>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Configuration</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Bath Type</th>
                    <th v-if="hasAmountColumn" class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Amount</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Challenge Material</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Duration</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Tag</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Status</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Actions</th>
                </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white">
                <template v-for="(config, index) in configurations" :key="config.key">
                <tr class="hover:bg-gray-50 transition-colors duration-150 relative">
                        <!-- Loading overlay for the entire row -->
                        <td v-if="isUpdating[index]" :colspan="tableColspan" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                            <div class="flex items-center space-x-2">
                                <svg class="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="text-sm text-gray-600">Updating...</span>
                            </div>
                        </td>
                        <!-- Configuration Title -->
                    <td class="whitespace-nowrap px-6 py-4">
                        <div class="font-medium text-gray-900">{{ getConfigurationTitle(config) }}</div>

                        <!-- Tags display for Standard Bath configurations only -->
                        <div v-if="isStandardBath(config)" class="mt-2 space-y-1">
                            <!-- Source Tags -->
                            <div>
                                <div class="text-xs text-gray-500 mb-1">From:</div>
                                <div class="flex flex-wrap gap-1">
                                    <span
                                        v-for="tag in config.sourceGroup?.tags?.filter(tag => tag.title !== 'Start Tag') || []"
                                        :key="`source-${tag.key}`"
                                        class="px-2 py-0.5 text-xs rounded-full opacity-75"
                                        :style="{
                                            backgroundColor: tag.backgroundColor,
                                            color: tag.textColor
                                        }"
                                    >
                                        {{ tag.title }}
                                    </span>
                                    <!-- Show "Unmarked" tag when no tags exist -->
                                    <span v-if="!config.sourceGroup?.tags?.filter(tag => tag.title !== 'Start Tag')?.length"
                                        class="px-2 py-0.5 text-xs rounded-full opacity-75 bg-gray-400 text-gray-700">
                                        Unmarked
                                    </span>
                                </div>
                            </div>

                            <!-- Destination Tags -->
                            <div v-if="getConfigurationTags(config).length > 0">
                                <div class="text-xs text-gray-500 mb-1">To:</div>
                                <div class="flex flex-wrap gap-1">
                                    <span
                                        v-for="tag in getConfigurationTags(config)"
                                        :key="`dest-${tag.key}`"
                                        class="px-2 py-0.5 text-xs rounded-full"
                                        :style="{
                                            backgroundColor: tag.backgroundColor,
                                            color: tag.textColor
                                        }"
                                    >
                                        {{ tag.title }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </td>

                    <!-- Bath Type -->
                    <td class="whitespace-nowrap px-6 py-4 text-sm">
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              :class="{
                                'bg-blue-100 text-blue-800': isSimplifiedBath(config),
                                'bg-green-100 text-green-800': isStandardBath(config),
                                'bg-gray-100 text-gray-800': !isSimplifiedBath(config) && !isStandardBath(config)
                              }">
                          {{ getBathTypeLabel(config) }}
                        </span>
                    </td>

                    <!-- Fish Amount -->
                    <td v-if="hasAmountColumn" class="whitespace-nowrap px-6 py-4 text-sm">
                        <div class="font-medium text-gray-900">
                            {{ config.targetAmount || 0 }}
                        </div>
                        <div v-if="config.targetWeightAmount" class="text-xs text-gray-500">
                            {{ config.targetWeightAmount }}g target weight
                        </div>
                    </td>


                    <!-- Challenge Materials -->
                    <td class="px-6 py-4 text-sm text-gray-900">
                        <div v-if="getMaterialsDisplay(config).length > 0">
                            <div v-for="(material, mIdx) in getMaterialsDisplay(config)" :key="mIdx" class="mb-2 last:mb-0">
                                <div class="font-medium">{{ material.name }}</div>
                                <div class="text-xs text-gray-500 space-x-2">
                                    <span>{{ material.method }}</span>
                                    <span>•</span>
                                    <span>{{ material.dose }} mL</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-gray-500">None</div>
                    </td>

                    <!-- Duration -->
                    <td class="px-6 py-4 text-sm text-gray-900">
                        <div class="flex items-center space-x-2">
                            <div class="flex flex-col">
                                <span class="font-medium">
                                    {{ formatDuration(config.challengeDuration) }} total
                                </span>
                                <span v-if="getProcessStatus(config).status === 'in_progress' && config.bathProcess?.startedAt" class="text-xs text-blue-600">
                                    {{ getElapsedTime(config.bathProcess.startedAt) }} elapsed
                                </span>
                                <span v-if="getProcessStatus(config).status === 'completed' && config.bathProcess?.startedAt && config.bathProcess?.completedAt" class="text-xs text-green-600">
                                    {{ getActualDuration(config.bathProcess.startedAt, config.bathProcess.completedAt) }} actual
                                </span>
                            </div>
                            <div v-if="config.requiresWeighing" class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                Weighing Required
                            </div>
                        </div>
                    </td>

                    <!-- Tag selection (only for standard bath) -->
                    <td class="whitespace-nowrap px-6 py-4 text-sm">
                        <div v-if="isStandardBath(config)" class="relative dropdown-container">
                            <button 
                                @click="toggleDropdown(index)"
                                :disabled="isUpdating[index]"
                                class="flex items-center justify-between w-full min-w-[150px] text-sm border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div class="flex items-center flex-1">
                                    <span 
                                        v-if="selectedTags[index]"
                                        class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                                        :style="{ 
                                            backgroundColor: selectedTags[index]?.backgroundColor || '#e5e7eb', 
                                            color: selectedTags[index]?.textColor || '#374151' 
                                        }"
                                    >
                                        {{ selectedTags[index]?.title }}
                                    </span>
                                    <span v-else class="text-gray-500">Select a tag...</span>
                                </div>
                                <svg class="h-4 w-4 text-gray-500 ml-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <div v-if="dropdownOpen[index]" class="absolute z-10 mt-2 w-full min-w-[200px] bg-white rounded-lg shadow-lg border border-gray-200">
                                <div class="py-2 max-h-48 overflow-y-auto">
                                    <button 
                                        v-for="tag in availableTagsStore.filter((tag: GroupTag) => tag.title !== 'Start Tag')" 
                                        :key="tag.key" 
                                        @click="selectTag(index, tag)"
                                        :disabled="isUpdating[index]"
                                        class="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span 
                                            class="inline-block px-2 py-1 text-xs font-medium rounded-full"
                                            :style="{ 
                                                backgroundColor: tag.backgroundColor || '#e5e7eb', 
                                                color: tag.textColor || '#374151' 
                                            }"
                                        >
                                            {{ tag.title }}
                                        </span>
                                    </button>
                                    <div v-if="availableTagsStore.filter((tag: GroupTag) => tag.title !== 'Start Tag').length === 0" class="px-3 py-2 text-sm text-gray-500">
                                        No tags available
                                    </div>
                                </div>
                            </div>
                        </div>
                        <span v-else class="text-gray-400">-</span>
                    </td>

                    <!-- Status -->
                    <td class="px-6 py-4 text-sm">
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              :class="{
                                'bg-gray-100 text-gray-800': getProcessStatus(config).color === 'gray',
                                'bg-blue-100 text-blue-800': getProcessStatus(config).color === 'blue',
                                'bg-green-100 text-green-800': getProcessStatus(config).color === 'green'
                              }">
                          {{ getProcessStatus(config).label }}
                        </span>
                    </td>

                    <!-- Actions -->
                    <td class="px-6 py-4 text-sm">
                        <div class="flex items-center space-x-2">
                            <!-- Start Process Button -->
                            <button
                                v-if="getProcessStatus(config).status === 'not_started'"
                                @click="handleStartProcess(config.key)"
                                class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                                Start Process
                            </button>

                            <!-- Finish Process Button -->
                            <button
                                v-if="getProcessStatus(config).status === 'in_progress'"
                                @click="handleFinishProcess(config.key)"
                                class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                            >
                                Finish Process
                            </button>

                            <!-- Record Weight Button -->
                            <button
                                v-if="shouldShowWeightButton(config)"
                                @click="handleRecordWeight(config)"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                title="Record Weight"
                            >
                                <WeightIcon />
                                <span class="ml-1">Record Weight</span>
                            </button>

                            <!-- Completed Status -->
                            <span v-if="getProcessStatus(config).status === 'completed'" class="text-green-600 font-medium">
                                ✓ Complete
                              </span>
                        </div>
                    </td>
                </tr>
                
                <!-- Special Instructions Row -->
                <tr v-if="config.instructions" class="bg-gray-50">
                    <td
                        :colspan="tableColspan"
                        :class="showInstructions[index] ? 'px-6 py-4' : 'px-6 py-1.5'"
                    >
                        <button
                            @click="toggleInstructions(index)"
                            class="w-full flex items-center justify-between text-left hover:bg-gray-100 rounded transition-colors duration-200"
                            :class="showInstructions[index] ? 'p-3' : 'p-1.5'"
                        >
                            <div class="flex items-center gap-2">
                                <svg class="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                </svg>
                                <span :class="showInstructions[index] ? 'text-sm font-semibold text-red-600' : 'text-xs font-semibold text-red-600'">
                                    Special Instructions
                                </span>
                            </div>
                            <svg 
                                class="w-4 h-4 text-gray-500 transition-transform duration-200"
                                :class="{ 'rotate-180': showInstructions[index] }"
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        <div
                            v-show="showInstructions[index]"
                            class="mt-2 p-4 bg-red-50 border border-red-200 rounded-lg transition-all duration-300 ease-in-out"
                        >
                            <p class="text-sm text-red-700 leading-relaxed whitespace-pre-wrap">
                                {{ config.instructions }}
                            </p>
                        </div>
                    </td>
                </tr>
                </template>
                </tbody>
            </table>
        </div>

        <!-- Instructions Note -->
        <div class="mt-4 p-4 bg-blue-50 rounded-md border border-blue-200">
            <h4 class="text-sm font-medium text-blue-800 mb-2">Bath Process Instructions:</h4>
            <ul class="text-sm text-blue-700 space-y-1">
                <li>• Click "Start Process" to begin the bath treatment</li>
                <li>• The process will run for the specified duration</li>
                <li>• Click "Finish Process" when ready to complete (justification required if early/late)</li>
            </ul>
        </div>
    </div>
    <JustificationModal />
</template>
